import React from 'react';
interface ScoreSelectorProps {
  attributeId: string;
  attributeName: string;
  currentScore: number;
  onChange: (attributeId: string, score: number) => void;
}
const ScoreSelector: React.FC<ScoreSelectorProps> = ({
  attributeId,
  attributeName,
  currentScore,
  onChange
}) => {
  const scores = [0, 2, 4, 6, 8, 10];
  // List of external characteristics attributes
  const externalAttributes = ['crustColor', 'crustPestana', 'crustCrispness', 'crustAppearance', 'integrity', 'symmetry'];
  // List of internal characteristics attributes (miolo)
  const internalAttributes = ['crumbAppearance', 'crumbColor', 'crumbTexture', 'crumbCellStructure'];
  // Function to get the appropriate image URL based on attribute and score
  const getImageUrl = (attributeId: string, score: number) => {
    // For all external attributes, use the pao10.jpg image for score 10
    if (externalAttributes.includes(attributeId) && score === 10) {
      return 'https://uploadthingy.s3.us-west-1.amazonaws.com/a4g6gKNkhD3xANUb3dbaKg/pao10.jpg';
    }
    // For all internal attributes, use the miolo10.jpg image for score 10
    if (internalAttributes.includes(attributeId) && score === 10) {
      return 'https://uploadthingy.s3.us-west-1.amazonaws.com/fe6bs3Kn7cwECZyhKtDbHZ/miolo10.jpg';
    }
    // Images for pestana (crustPestana)
    if (attributeId === 'crustPestana') {
      switch (score) {
        case 0:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/qJfeUysU2bVfs5MNT6hA7p/pp0.jpg';
        case 2:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/588eYeedqrdtwAWg21HHhi/pp2.jpg';
        case 4:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/6LFrNTNPfi37qTwtZHxbQY/pp4.jpg';
        case 6:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/7GLijZ6T4Y5YepHUNZcWKG/pp6.jpg';
        case 8:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/ocLEYVvfY6kaMBZhf7bhGE/pp8.jpg';
        default:
          return `https://via.placeholder.com/150?text=${attributeName}+${score}`;
      }
    }
    // Images for crust crispness (crocância)
    if (attributeId === 'crustCrispness') {
      switch (score) {
        case 0:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/iYHFhxvkhuupTPUfLkzW14/cr0.jpg';
        case 2:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/tUYCmohUeVUdR35DhYD5PA/cr2.jpg';
        case 4:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/deMC4ASeVQ2sUrLikkETzQ/cr4.jpg';
        case 6:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/3hrtCR1m3S6LNE2FPKhpB6/cr6.jpg';
        case 8:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/o2DWncBe2z6etm1ZEygswm/cr8.jpg';
        default:
          return `https://via.placeholder.com/150?text=${attributeName}+${score}`;
      }
    }
    // Images for crust appearance (aspecto)
    if (attributeId === 'crustAppearance') {
      switch (score) {
        case 0:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/w5TWroc5HnWFxighJVXM64/as0.jpg';
        case 2:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/2WRpXa5i355ykiNGSMxQnA/as2.jpg';
        case 4:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/kMT1nSofrazGiMsJFWjhe6/as4.jpg';
        case 6:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/6RwAjs6tZexN5vXC7MJiNM/as6.jpg';
        case 8:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/p15DurvB4EsdxY9aM4sn9c/as8.jpg';
        default:
          return `https://via.placeholder.com/150?text=${attributeName}+${score}`;
      }
    }
    // Images for crumb appearance (aspecto da crosta)
    if (attributeId === 'crumbAppearance') {
      switch (score) {
        case 0:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/i1FQwgHkD2ABXjX5LLQ9xY/as0.jpg';
        case 2:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/7TFEmBmrqwTS8y6nffz1rW/as2.jpg';
        case 4:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/su2qRYQunYY8Mkk7LDkqGq/as4.jpg';
        case 6:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/aZq3B698A2jz559RDStojH/as6.jpg';
        case 8:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/dDBpsu6ESB29Bqw5sYdGk1/as8.jpg';
        default:
          return `https://via.placeholder.com/150?text=${attributeName}+${score}`;
      }
    }
    // Images for crumb color (cor)
    if (attributeId === 'crumbColor') {
      switch (score) {
        case 0:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/5tBWisBJpVvzxKGUSCPenq/co0.jpg';
        case 2:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/jDcK6JYJ1sHRQGcacUEdxf/co2.jpg';
        case 4:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/j2rqh7cEExa3BUxEsum2Hv/co4.jpg';
        case 6:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/czAM1Lx2kijBQTrhLw8ggg/co6.jpg';
        case 8:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/tuF8kjxQ2Hb6hgsnKpGD9b/co8.jpg';
        default:
          return `https://via.placeholder.com/150?text=${attributeName}+${score}`;
      }
    }
    // Images for crumb texture (textura)
    if (attributeId === 'crumbTexture') {
      switch (score) {
        case 0:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/ffiYkyRcGaPzn3yo751G8E/tx0.jpg';
        case 2:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/ve9cFJYC45jELuhxJ1kK7n/tx2.jpg';
        case 4:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/91Nmq12NiLfW1cLoJKbzVH/tx4.jpg';
        case 6:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/q4EZ2LfAV8yEG5hanhxbxg/tx6.jpg';
        case 8:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/1c6c4rRyVHajp6axtd4aTa/tx8.jpg';
        default:
          return `https://via.placeholder.com/150?text=${attributeName}+${score}`;
      }
    }
    // Images for crumb cell structure (estrutura de células)
    if (attributeId === 'crumbCellStructure') {
      switch (score) {
        case 0:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/mJwFxA6jpSSugu6s2WkiEr/es0.jpg';
        case 2:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/jRhjeZifdRy3LDjwK3LdpE/es2.jpg';
        case 4:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/4PnJHwHR3yvCU9Suyun3fX/es4.jpg';
        case 6:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/sPD6uWh8Nfk5K4X5KtdhsX/es6.jpg';
        case 8:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/4AL2EM29o2onP8G8aAHKix/es8.jpg';
        default:
          return `https://via.placeholder.com/150?text=${attributeName}+${score}`;
      }
    }
    // Images for integrity (integridade)
    if (attributeId === 'integrity') {
      switch (score) {
        case 0:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/3tLL5asKmAb3RbGxHzkv8p/in0.jpg';
        case 2:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/2jQXDpNKDSsMYbzEGZhenN/in2.jpg';
        case 4:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/2B61qsXdge8SkUAXg54cFJ/in4.jpg';
        case 6:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/jLuWmaJSLAT9ftk1C1b3kY/in6.jpg';
        case 8:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/2WgTv6UBoCdjnRqjftMGaq/in8.jpg';
        default:
          return `https://via.placeholder.com/150?text=${attributeName}+${score}`;
      }
    }
    // Images for symmetry (simetria)
    if (attributeId === 'symmetry') {
      switch (score) {
        case 0:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/dvbGC6VyEMmCZiD2dJz1EK/sm0.jpg';
        case 2:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/x942uxNXS6r3uFTUQwjcco/sm2.jpg';
        case 4:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/iRk4UgFmX7PLwckN5gQEXi/sm4.jpg';
        case 6:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/o71GJcrc6n1583SGjncep8/sm6.jpg';
        case 8:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/9ScY65QwENs1CWkxB3vD8h/sm8.jpg';
        default:
          return `https://via.placeholder.com/150?text=${attributeName}+${score}`;
      }
    }
    // Real images for crust color
    if (attributeId === 'crustColor') {
      switch (score) {
        case 0:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/eUHGG8DtcVnx5P9EoaW93z/cc0.jpg';
        case 2:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/a6bWbz2Kar7fafYoiK6m37/cc2.jpg';
        case 4:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/k1aZs9Uw8AxAF9moKmEitw/cc4.jpg';
        case 6:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/aUW8sNcv9gbijd8kJLnaP2/cc6.jpg';
        case 8:
          return 'https://uploadthingy.s3.us-west-1.amazonaws.com/iofJb1Shq6gRccZkN62UQH/cc8.jpg';
        default:
          return `https://via.placeholder.com/150?text=${attributeName}+${score}`;
      }
    }
    // For other attributes, use placeholder images until real images are available
    return `https://via.placeholder.com/150?text=${attributeName}+${score}`;
  };
  return <div className="mb-8" data-id="element-33">
      <h3 className="text-lg font-semibold mb-3 text-gray-800" data-id="element-34">
        {attributeName}
      </h3>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4" data-id="element-35">
        {scores.map(score => <div key={score} className="flex flex-col items-center" data-id="element-36">
            <div className={`w-full aspect-square border-2 rounded-md overflow-hidden cursor-pointer transition duration-200 ${currentScore === score ? 'border-orange-500 shadow-md' : 'border-gray-200 hover:border-orange-300'}`} onClick={() => onChange(attributeId, score)} data-id="element-37">
              <img src={getImageUrl(attributeId, score)} alt={`${attributeName} - ${score} pontos`} className="w-full h-full object-cover" data-id="element-38" />
            </div>
            <span className={`mt-1 font-medium ${currentScore === score ? 'text-orange-500' : 'text-gray-600'}`} data-id="element-39">
              {score}
            </span>
          </div>)}
      </div>
      <div className="flex justify-between text-xs text-gray-500 px-1" data-id="element-40">
        <span data-id="element-41">Inadequado</span>
        <span data-id="element-42">Excelente</span>
      </div>
    </div>;
};
export default ScoreSelector;